import { BossEncounterContent, BossEncounterRewardPolicyContent } from '../data';
import { Logger, createLogger } from '../utils/Logger';

export const BOSS_ENCOUNTER_METADATA_KEY = 'bossEncounterMetadata' as const;

export interface BossEncounterResolvedStats {
  readonly health?: number;
  readonly maxHealth?: number;
  readonly speed?: number;
  readonly damage?: number;
  readonly xpReward?: number;
}

export interface BossEncounterEventMetadata {
  readonly encounterId: string;
  readonly enemyId: string;
  readonly enemyName?: string;
  readonly enemyNameKey?: string;
  readonly displayName?: string;
  readonly displayNameKey?: string;
  readonly warningTextKey?: string;
  readonly rewardPolicy?: BossEncounterRewardPolicyContent;
  readonly requiredForVictory: boolean;
  readonly resolvedStats?: BossEncounterResolvedStats;
}

export interface EnemyHitEventPayload {
  readonly enemy: unknown;
  readonly id: string;
  readonly damage: number;
  readonly currentHealth: number;
  readonly maxHealth: number;
  readonly kind: 'primary' | 'chain' | 'area';
  readonly critical: boolean;
  readonly effects?: {
    readonly burn?: { damagePerSecond: number; expiresAt: number };
    readonly slow?: { multiplier: number; expiresAt: number };
  };
  readonly x: number;
  readonly y: number;
  readonly bossEncounter?: BossEncounterEventMetadata;
}

export interface EnemyKilledEventPayload {
  readonly enemy: unknown;
  readonly x: number;
  readonly y: number;
  readonly id: string;
  readonly xpValue: number;
  readonly bossEncounter?: BossEncounterEventMetadata;
}

interface DataBackedSpriteLike {
  getData?(key: string): unknown;
  setData?(key: string, value: unknown): unknown;
}

export function createBossEncounterEventMetadata(
  encounter: BossEncounterContent,
  resolvedStats?: BossEncounterResolvedStats,
  fallback?: {
    enemyName?: string;
    enemyNameKey?: string;
  }
): BossEncounterEventMetadata {
  return {
    encounterId: encounter.id,
    enemyId: encounter.enemyId,
    enemyName: fallback?.enemyName,
    enemyNameKey: fallback?.enemyNameKey,
    displayName: encounter.displayName,
    displayNameKey: encounter.displayNameKey,
    warningTextKey: encounter.warningTextKey,
    rewardPolicy: encounter.rewardPolicy,
    requiredForVictory: encounter.requiredForVictory !== false,
    resolvedStats
  };
}

export function assignBossEncounterMetadata(target: DataBackedSpriteLike, metadata: BossEncounterEventMetadata): void {
  target.setData?.(BOSS_ENCOUNTER_METADATA_KEY, metadata);
}

export function resolveBossEncounterMetadata(
  target: DataBackedSpriteLike | null | undefined,
  logger: Logger = createLogger('BossEventPayloads')
): BossEncounterEventMetadata | undefined {
  const raw = target?.getData?.(BOSS_ENCOUNTER_METADATA_KEY);
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  const payload = raw as Partial<BossEncounterEventMetadata>;
  if (!payload.encounterId || !payload.enemyId) {
    logger.warn('resolveBossEncounterMetadata:missing-required-fields', { raw });
    return undefined;
  }

  return {
    encounterId: payload.encounterId,
    enemyId: payload.enemyId,
    enemyName: payload.enemyName,
    enemyNameKey: payload.enemyNameKey,
    displayName: payload.displayName,
    displayNameKey: payload.displayNameKey,
    warningTextKey: payload.warningTextKey,
    rewardPolicy: payload.rewardPolicy,
    requiredForVictory: payload.requiredForVictory !== false,
    resolvedStats: payload.resolvedStats
  };
}

export function isBossLookingPayload(data: { id?: unknown; enemy?: DataBackedSpriteLike } | null | undefined): boolean {
  if (!data) {
    return false;
  }

  const aiPattern = data.enemy?.getData?.('aiPattern');
  if (aiPattern === 'boss') {
    return true;
  }

  const id = typeof data.id === 'string' ? data.id : undefined;
  return typeof id === 'string' && id.toLowerCase().includes('boss');
}
